import React, { useCallback, useMemo } from "react";
import { findBounds, MAP_MAX_ZOOM, MAP_CENTER, EMPTY_ARRAY, MAP_MIN_ZOOM } from "../misc";
import ReactDOM from "react-dom";
import { BaseMap } from "./BaseMap";
import { ColorMappings, SpatialValue } from "./types";
import { LayerInfo, IS_SCHEME_GEOJSON } from "./Map";
import { LongText, NDValueStatusDomain } from "../../Components/processors";
import { Paragraph } from "../Paragraph";

import _ from "lodash";
import styles from "./SupplyDemandProviderMap.module.scss";

import mapboxgl from "mapbox-gl";

const PWS = "Potable water scheme";

export const colorMappings: ColorMappings = {
    high: "#11704C",
    fair: "#1AC1C1",
    unsure: "#E8C589",
    low: "#C95C12",
    "very low": "#84371C",
    providerGeometry: "#BED2C5",
    default: "#777779"
} as unknown as ColorMappings;

export const confidenceLevelToNumber = {
    high: 5,
    fair: 4,
    unsure: 3,
    low: 2,
    "very low": 1
};

const maxBounds = [123.57421875, -29.06597313798418, 164.443359375, -9.690228926463384];

export const SupplyDemandProviderMapLegend: React.FC<{}> = () => {
    const entries = _.orderBy(Object.entries(confidenceLevelToNumber), d => d[1], "desc");
    return (
        <div className={`${styles.legendContainer} sd-legend-container`}>
            {entries?.map((d, index) => {
                return (
                    <div className={styles.legendItem} key={index}>
                        <div
                            className={styles.legendColorBox}
                            style={{
                                backgroundColor: colorMappings[d?.[0]]
                            }}
                        >
                            {d[1]}
                        </div>
                        <div className={`ml-2 ${styles.legendName}`}>{d[0]}</div>
                    </div>
                );
            })}
        </div>
    );
};

export interface SupplyDemandMapMetadata {
    schemeMetadata: {
        comments?: LongText[];
        longLabel: string;
        schemeName: string;
        schemeId: string;
    }[];
    providerName?: string;
}

export interface SupplyDemandProviderMapProps {
    providerAdminId?: string;
    providerName?: string;
    providerMapName?: string;
    spatialValues?: SpatialValue[];
    metadata?: SupplyDemandMapMetadata;
}

const LSupplyDemandProviderMap: React.FC<SupplyDemandProviderMapProps> = ({
    // providerName,
    providerMapName,
    providerAdminId,
    metadata,
    spatialValues = EMPTY_ARRAY as SpatialValue[]
}) => {
    const displayAdminIds = useMemo(() => {
        const spatialValueAdminIds = spatialValues?.map(d => d.adminId) ?? EMPTY_ARRAY;

        // return [adminId];
        return providerAdminId == null || providerAdminId.trim() === ""
            ? EMPTY_ARRAY
            : _.flattenDeep([spatialValueAdminIds, providerAdminId]).filter(d => d != null && d.trim() !== "");
    }, [providerAdminId, spatialValues]);

    const selectedAdminId = undefined;
    const flyToAdminIds = useMemo(() => {
        return [providerAdminId].filter(d => d != null && d.trim() !== "");
    }, [providerAdminId]);

    const layers = useMemo(
        () => {
            // const schemeLayer: DataLayer = {
            //     ...LayerInfo.scheme,
            //     id: `${LayerInfo.scheme.id}-ND`,
            //     featureGroupingSelection: ["in", "lookup", _.join([providerName, PWS], ` ${SEPARATOR} `)]
            // };

            return [
                LayerInfo.provider,
                // schemeLayer
                LayerInfo.scheme
            ];
        },
        [
            // providerName
        ]
    );

    // setup symbol layers
    const setupExtraLayers: (map: mapboxgl.Map) => void = useCallback(
        map => {
            // const schemeNames = _.uniq(spatialValues?.map(d => d.adminId) ?? (EMPTY_ARRAY as string[])).filter(
            //     d => d != null && d.trim() !== ""
            // );

            // // setup MD symbol
            // const mdLayer = map.getLayer(`${LayerInfo.scheme.id}-md-symbol`);
            // const schemeLayer = map.getLayer(LayerInfo.scheme.id);
            // !mdLayer &&
            //     schemeLayer &&
            //     map.addLayer(
            //         {
            //             id: `${LayerInfo.scheme.id}-md-symbol`,
            //             source: LayerInfo.scheme.source,
            //             "source-layer": LayerInfo.scheme["source-layer"],
            //             filter: ["all", false],
            //             type: "symbol",
            //             layout: {
            //                 "text-allow-overlap": true,
            //                 "text-offset": [0, 0.05],
            //                 "text-field": VALUE_STATUS_NAMING.MD,
            //                 "text-font": ["Lato Bold", "Arial Unicode MS Bold"],
            //                 "text-size": 10
            //             },
            //             paint: {
            //                 "text-color": "#FFFFFF"
            //             }
            //         },
            //         LayerInfo.scheme.id
            //     );

            // // map.moveLayer()

            // map.getLayer(`${LayerInfo.scheme.id}-md-symbol`) != null &&
            //     map.setFilter(
            //         `${LayerInfo.scheme.id}-md-symbol`,
            //         [
            //             "all",
            //             schemeNames?.length > 0
            //                 ? ["match", ["get", "Scheme"], ["all", ...schemeNames], false, true]
            //                 : null,
            //             ["match", ["get", "lookup"], ["all", `${providerName} ### ${PWS}`], true, false]
            //         ].filter(d => d != null)
            //     );

            // setup confidence symbol layers
            const confidenceToSchemeIds = spatialValues.reduce<Record<string, string[]>>((prev, current) => {
                const category = current?.category;
                let confidence =
                    NDValueStatusDomain[category] === 1 ? category : confidenceLevelToNumber[category] ?? undefined;

                const schemeId = current?.adminId;
                // const schemeName = current?.name;

                if (confidence == null) return prev;

                prev[confidence] = [...(prev[confidence] ?? []), schemeId];
                // prev[confidence] = [...(prev[confidence] ?? []), schemeName];

                return prev;
            }, {});

            // clear all confidence level layers
            [
                // ..._.keys(NDValueStatusDomain),
                ..._.values(confidenceLevelToNumber)
            ].forEach(d => {
                const layerId = `${LayerInfo.scheme.id}-confidence-${d}-symbol`;
                const confLayer = map.getLayer(layerId);
                confLayer && map.setFilter(layerId, ["all", false]);
            });

            Object.entries(confidenceToSchemeIds).forEach(val => {
                const confidence = val[0];
                const schemeIds = _.uniq(val[1] ?? EMPTY_ARRAY);

                const layerId = `${LayerInfo.scheme.id}-confidence-${confidence}-symbol`;

                const confLayer = map.getLayer(layerId);

                !confLayer &&
                    map.addLayer({
                        id: layerId,
                        source: LayerInfo.scheme.source,

                        ...(!IS_SCHEME_GEOJSON ? { "source-layer": LayerInfo.scheme["source-layer"] } : {}),

                        // filter: ["any", ["in", "Scheme", "Bessie Point"]],
                        filter: ["any", false],
                        type: "symbol",
                        layout: {
                            "text-offset": [0, 0.05],
                            "text-allow-overlap": true,
                            "text-field": confidence,
                            "text-size": 13,
                            "text-font": ["Lato Bold", "Arial Unicode MS Bold"]
                        },
                        paint: {
                            "text-color": "#FFFFFF"
                        }
                    });

                map.setFilter(
                    layerId,
                    [
                        "all",
                        schemeIds?.length > 0
                            ? ["match", ["get", "Scheme ID"], ["all", ...schemeIds], true, false]
                            : false,
                        // schemeIds?.length > 0
                        //     ? ["match", ["get", "Scheme"], ["all", ...schemeIds], true, false]
                        //     : false,
                        ["match", ["get", "lookup"], ["all", `${providerMapName} ### ${PWS}`], true, false]
                    ].filter(d => d != null)
                );
            });

            // window["map"] = map;
        },
        [spatialValues, providerMapName]
    );

    return (
        <>
            <BaseMap
                labelLayerDisplay={false}
                oceanLayerDisplay={false}
                scrollZoom={false}
                fitBoundsSettings={{
                    padding: {
                        top: 30,
                        bottom: 30,
                        left: 10,
                        right: 10
                    }
                }}
                setupExtraLayers={setupExtraLayers}
                disablePolygonHoverStyles={true}
                meta={metadata as unknown as Record<string, unknown>}
                displayAdminIds={displayAdminIds}
                selectedAdminId={selectedAdminId}
                minZoom={MAP_MIN_ZOOM}
                maxZoom={MAP_MAX_ZOOM}
                center={MAP_CENTER}
                flyToAdminIds={flyToAdminIds}
                // maxBounds={DEFAULT_MAX_BOUNDS}
                maxBounds={maxBounds}
                hover={({ d3Tooltip, meta, data, features }) => {
                    const element = d3Tooltip && d3Tooltip.node ? d3Tooltip.node() : null;
                    const metadata = meta as unknown as SupplyDemandMapMetadata;
                    const feature = features?.[0];

                    let schemeName = feature?.properties?.["Scheme"] as string;
                    let schemeId = feature?.properties?.["Scheme ID"] as string;
                    let schemeLookupFieldValue = feature?.properties?.["lookup"] as string;
                    let content = (
                        <>{schemeLookupFieldValue == null ? metadata.providerName : schemeName ?? "Unknown"}</>
                    );

                    if (data && meta) {
                        const spatialValue = data as SpatialValue;
                        const metadata = meta as unknown as SupplyDemandMapMetadata;

                        const { name: schemeNameFromValue, category: confidenceLevel } = spatialValue;
                        const { comments, longLabel } =
                            metadata?.schemeMetadata?.find(d => d.schemeId === schemeId) ?? {};

                        schemeName = `${schemeNameFromValue ?? schemeName} ${
                            confidenceLevelToNumber[confidenceLevel] != null ? "Potable Water Scheme" : ""
                        }`;

                        content = (
                            <>
                                {schemeName}
                                {confidenceLevelToNumber[confidenceLevel] != null && longLabel != null && (
                                    <>
                                        <br />
                                        {`${longLabel}: ${confidenceLevel.toString().toUpperCase()}`}
                                    </>
                                )}{" "}
                                {comments != null && comments.length > 0 && (
                                    <>
                                        <br />
                                        <span>Comments:</span>
                                        <br />
                                        {comments.map((comment, index) => {
                                            return (
                                                <Paragraph
                                                    key={index}
                                                    nid={-1}
                                                    status={true}
                                                    title={""}
                                                    text={comment}
                                                />
                                            );
                                        })}
                                    </>
                                )}{" "}
                            </>
                        );
                    }
                    ReactDOM.render(content, element);
                }}
                layers={
                    layers
                    //[
                    //LayerInfo.provider,
                    //LayerInfo.scheme
                    // LayerInfo.dynamicScheme
                    //]
                }
                colorMappings={colorMappings}
                spatialValues={spatialValues ?? EMPTY_ARRAY}
                findBounds={findBounds}
                onFeatureClick={undefined}
            />
        </>
    );
};

export const SupplyDemandProviderMap = React.memo(LSupplyDemandProviderMap, (prev, next) => {
    return (
        prev?.providerAdminId === next?.providerAdminId &&
        prev?.providerMapName === next?.providerMapName &&
        prev?.providerName === next?.providerName &&
        prev?.metadata === next?.metadata &&
        prev?.spatialValues === next?.spatialValues
    );
});
